import { Link } from '@tanstack/react-router'

import type { Notification } from '@/bindings/Notification'
import { Badge } from '@/components/badge'
import { renderNotification, useReadOne } from '@/features/notifications/api'
import type { System } from '@/lib/auth-store'
import { cx } from '@/lib/cx'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatDateTime } from '@/lib/time'

/**
 * One notice, on the page or in the bell's panel: a link where it leads
 * (opening it marks it read), else a plain card. Unread ones are marked.
 */
export function NotificationRow({
  system,
  item,
  compact = false,
  onOpen,
}: {
  system: System
  item: Notification
  compact?: boolean
  onOpen?: () => void
}) {
  const t = useT()
  const language = useLanguage()
  const readOne = useReadOne(system)
  const { text, link } = renderNotification(system, item, t, language)
  const unread = item.read_at === null
  const body = (
    <>
      <span className="flex flex-wrap items-center gap-2">
        <span className={cx(unread && 'font-medium', compact && 'text-sm')}>{text}</span>
        {unread && <Badge tone="success">{t('notification.new')}</Badge>}
      </span>
      <span className={cx('text-muted', compact ? 'text-xs' : 'text-sm')}>{formatDateTime(item.created_at, language)}</span>
    </>
  )
  const className = cx(
    'bg-surface flex flex-col gap-1 border',
    compact ? 'rounded-lg p-3' : 'rounded-xl p-4',
    unread ? 'border-accent' : 'border-border',
  )
  return link ? (
    <Link
      {...link}
      onClick={() => {
        if (unread) readOne.mutate(item.id)
        onOpen?.()
      }}
      className={cx(className, 'hover:bg-accent-soft')}
    >
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  )
}
